import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  CreditCard,
  Clock,
  TicketIcon,
  DollarSign,
  TrendingUp,
  Building2,
  Shield,
  Activity,
  Sparkles,
  Wallet,
  PiggyBank,
  AlertTriangle,
  Bell,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CurrencyConverter } from "@/components/currency-converter";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useToast } from "@/hooks/use-toast";

interface DashboardKPIs {
  totalCustomers: number;
  activeCards: number;
  totalTransactions: number;
  totalVolumeCents: number;
  pendingKyc: number;
  openTickets: number;
  amountFundedCents: number;
  remainingBalanceCents: number;
}

function formatCompactNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + "M";
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + "K";
  }
  return num.toString();
}

interface OrgAlertSettings {
  lowBalanceAlertCents: number | null;
  lowBalanceAlertEnabled: boolean;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const { toast } = useToast();

  const [, navigate] = useLocation();
  const [alertConfigOpen, setAlertConfigOpen] = useState(false);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [alertAmountInput, setAlertAmountInput] = useState("");

  // Fetch org alert settings
  const { data: orgSettings } = useQuery<OrgAlertSettings>({
    queryKey: ["org-alert-settings"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/organization/settings");
      if (!response.ok) throw new Error("Failed to fetch org settings");
      return response.json();
    },
  });

  const saveAlertMutation = useMutation({
    mutationFn: async (data: { lowBalanceAlertCents: number; lowBalanceAlertEnabled: boolean }) => {
      const response = await apiRequest("PATCH", "/api/organization/settings/low-balance-alert", data);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to save alert settings");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-alert-settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organization/settings"] });
      toast({ title: "Saved", description: "Low balance alert settings updated" });
      setAlertConfigOpen(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const openAlertConfig = () => {
    setAlertEnabled(orgSettings?.lowBalanceAlertEnabled ?? false);
    setAlertAmountInput(
      orgSettings?.lowBalanceAlertCents != null ? (orgSettings.lowBalanceAlertCents / 100).toString() : ""
    );
    setAlertConfigOpen(true);
  };

  const handleSaveAlert = () => {
    const cents = Math.round(parseFloat(alertAmountInput) * 100);
    if (alertEnabled && (isNaN(cents) || cents <= 0)) {
      toast({ title: "Validation Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    saveAlertMutation.mutate({ lowBalanceAlertCents: cents, lowBalanceAlertEnabled: alertEnabled });
  };

  const { data: kpis, isLoading } = useQuery<DashboardKPIs>({
    queryKey: ["dashboard-kpis"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/dashboard/summary");
      if (!response.ok) throw new Error("Failed to fetch KPIs");
      return response.json();
    },
  });

  // Determine if low balance alert should show
  const isLowBalance =
    orgSettings?.lowBalanceAlertEnabled &&
    orgSettings.lowBalanceAlertCents != null &&
    kpis?.remainingBalanceCents != null &&
    kpis.remainingBalanceCents <= orgSettings.lowBalanceAlertCents;

  const canConfigureAlert = user?.role === "SUPER" || user?.role === "ORGANIZATION";

  const primaryKpis = [
    {
      title: "Total Volume",
      value: formatCurrency(kpis?.totalVolumeCents ?? 0),
      icon: DollarSign,
      isPrimary: true,
      section: "total-volume",
    },
    {
      title: "Active Cards",
      value: kpis?.activeCards ?? 0,
      icon: CreditCard,
      isPrimary: false,
      section: "active-cards",
    },
    {
      title: "Transactions",
      value: formatCompactNumber(kpis?.totalTransactions ?? 0),
      icon: Activity,
      isPrimary: false,
      section: "transactions",
    },
  ];

  const secondaryKpis = [
    {
      title: "Customers",
      value: kpis?.totalCustomers ?? 0,
      display: (v: number | string) => typeof v === "number" ? v.toLocaleString() : v,
      icon: Users,
      color: "text-primary",
      bgColor: "bg-primary/10",
      borderColor: "border-primary/20",
      section: "customers",
    },
    {
      title: "Pending KYC",
      value: kpis?.pendingKyc ?? 0,
      display: (v: number | string) => typeof v === "number" ? v.toLocaleString() : v,
      icon: Clock,
      color: "text-warning-foreground",
      bgColor: "bg-warning/10",
      borderColor: "border-warning/20",
      section: "pending-kyc",
    },
    {
      title: "Open Tickets",
      value: kpis?.openTickets ?? 0,
      display: (v: number | string) => typeof v === "number" ? v.toLocaleString() : v,
      icon: TicketIcon,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
      borderColor: "border-destructive/20",
      section: "open-tickets",
    },
  ];

  const fundingKpis = [
    {
      title: "Amount Funded",
      value: formatCurrency(kpis?.amountFundedCents ?? 0),
      display: (v: number | string) => v,
      icon: Wallet,
      color: "text-emerald-600 dark:text-emerald-400",
      bgColor: "bg-emerald-500/10",
      borderColor: "border-emerald-500/30",
      description: "Total across all wallets",
      section: "amount-funded",
      onClick: () => navigate("/analytics?section=amount-funded"),
    },
    {
      title: "Remaining Balance",
      value: formatCurrency(kpis?.remainingBalanceCents ?? 0),
      display: (v: number | string) => v,
      icon: PiggyBank,
      color: "text-teal-600 dark:text-teal-400",
      bgColor: "bg-teal-500/10",
      borderColor: "border-teal-500/30",
      description: "Available across all wallets",
      section: "remaining-balance",
      onClick: () => navigate("/analytics?section=remaining-balance"),
    },
  ];

  const getRoleConfig = (role: string) => {
    switch (role) {
      case "SUPER":
        return {
          title: "Platform Administrator",
          description: "Full access to all platform features and settings",
          icon: Shield,
          badge: "Super Admin",
          badgeVariant: "premium" as const,
        };
      case "ORGANIZATION":
        return {
          title: "Organization",
          description: "Manage corporate admins and monitor card programs",
          icon: Building2,
          badge: "Program Mgr",
          badgeVariant: "default" as const,
        };
      case "BUSINESS_UNIT":
        return {
          title: "Business Unit",
          description: "Issue cards and manage employee spending",
          icon: Users,
          badge: "Business Unit",
          badgeVariant: "secondary" as const,
        };
      case "FINANCE":
        return {
          title: "Finance Analyst",
          description: "Access reports and financial analytics",
          icon: TrendingUp,
          badge: "Finance",
          badgeVariant: "success" as const,
        };
      default:
        return null;
    }
  };

  const roleConfig = user?.role ? getRoleConfig(user.role) : null;

  return (
    <div className="p-6 space-y-8 max-w-7xl mx-auto">
      {/* RC Launch Banner */}
      <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 via-emerald-500/5 to-teal-500/5 p-4 animate-reveal-up">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                v1.0.0-rc.3 is live
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                VedaLogic Card Management Platform — production release candidate
              </p>
            </div>
          </div>
          <Badge variant="outline" className="shrink-0 border-primary/30 text-primary font-mono text-xs">
            RC.3
          </Badge>
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 animate-reveal-up">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-display font-bold tracking-tight">Executive Dashboard</h1>
            {roleConfig && (
              <Badge variant={roleConfig.badgeVariant}>
                {roleConfig.badge}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Welcome back, <span className="font-medium text-foreground">{user?.name?.split(" ")[0] || "User"}</span>
          </p>
        </div>
      </div>

      {/* Low Balance Alert Banner */}
      {isLowBalance && (
        <div className="flex items-center gap-3 p-4 rounded-lg border border-amber-500/40 bg-amber-500/10 animate-reveal-up">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Low Balance Alert</p>
            <p className="text-xs text-amber-600 dark:text-amber-400/80">
              Remaining balance ({formatCurrency(kpis?.remainingBalanceCents ?? 0)}) is below your configured threshold of {formatCurrency(orgSettings?.lowBalanceAlertCents ?? 0)}. Please top up your account.
            </p>
          </div>
          {canConfigureAlert && (
            <Button variant="outline" size="sm" className="border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10" onClick={openAlertConfig}>
              <Settings className="h-3.5 w-3.5 mr-1" />
              Configure
            </Button>
          )}
        </div>
      )}

      {/* Primary KPIs - Premium stat cards */}
      <div className="grid gap-6 md:grid-cols-3">
        {primaryKpis.map((kpi, index) => (
          <Card
            key={kpi.title}
            variant={kpi.isPrimary ? "stat-gold" : "stat"}
            className={cn(
              "relative overflow-hidden transition-all duration-300 hover:-translate-y-1 animate-reveal-up cursor-pointer",
              kpi.isPrimary && "shadow-glow-gold"
            )}
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={() => navigate(`/analytics?section=${kpi.section}`)}
          >
            {/* Gradient overlay for primary card */}
            {kpi.isPrimary && (
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5" />
            )}

            <CardContent className="p-6 relative">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
                    {kpi.isPrimary && (
                      <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                    )}
                  </div>
                  {isLoading ? (
                    <Skeleton className="h-10 w-36" />
                  ) : (
                    <p className={cn(
                      "text-4xl font-display font-bold tracking-tight",
                      kpi.isPrimary ? "text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500" : "text-foreground"
                    )}>
                      {typeof kpi.value === "number" ? kpi.value.toLocaleString() : kpi.value}
                    </p>
                  )}
                </div>
                <div className={cn(
                  "p-3 rounded-xl transition-transform duration-300 group-hover:scale-110",
                  kpi.isPrimary
                    ? "bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/20"
                    : "bg-primary/10 border border-primary/10"
                )}>
                  <kpi.icon className={cn(
                    "h-5 w-5",
                    kpi.isPrimary ? "text-amber-500" : "text-primary"
                  )} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Secondary KPIs - Compact cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {secondaryKpis.map((kpi, index) => (
          <Card
            key={kpi.title}
            className={cn(
              "border-l-4 transition-all duration-300 hover:shadow-md animate-reveal-up cursor-pointer",
              kpi.borderColor
            )}
            style={{ animationDelay: `${0.3 + index * 0.1}s` }}
            onClick={() => navigate(`/analytics?section=${kpi.section}`)}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className={cn("p-3 rounded-xl", kpi.bgColor)}>
                  <kpi.icon className={cn("h-5 w-5", kpi.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
                  {isLoading ? (
                    <Skeleton className="h-7 w-16 mt-1" />
                  ) : (
                    <p className="text-2xl font-display font-bold">{kpi.display(kpi.value)}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Funding KPIs - Amount Funded & Remaining Balance */}
      <div className="grid gap-4 md:grid-cols-2">
        {fundingKpis.map((kpi, index) => (
          <Card
            key={kpi.title}
            className={cn(
              "border-l-4 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 animate-reveal-up cursor-pointer hover:ring-2 hover:ring-primary/20",
              kpi.borderColor,
              kpi.title === "Remaining Balance" && isLowBalance && "border-l-amber-500 ring-1 ring-amber-500/30"
            )}
            style={{ animationDelay: `${0.6 + index * 0.1}s` }}
            onClick={kpi.onClick}
          >
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <div className={cn("p-3 rounded-xl", kpi.bgColor)}>
                  <kpi.icon className={cn("h-5 w-5", kpi.color)} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-muted-foreground">{kpi.title}</p>
                  {isLoading ? (
                    <Skeleton className="h-7 w-32 mt-1" />
                  ) : (
                    <p className={cn("text-2xl font-display font-bold", kpi.color)}>
                      {kpi.value}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {kpi.description}
                    <span className="ml-1 text-emerald-600 dark:text-emerald-400">— View analytics</span>
                  </p>
                </div>
                {kpi.title === "Remaining Balance" && canConfigureAlert && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); openAlertConfig(); }}
                    title="Configure low balance alert"
                  >
                    <Bell className={cn("h-4 w-4", orgSettings?.lowBalanceAlertEnabled && "text-amber-500")} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Role-specific card */}
      {roleConfig && (
        <Card
          variant={user?.role === "SUPER" ? "premium" : "elevated"}
          className={cn(
            "relative overflow-hidden animate-reveal-up",
            user?.role === "SUPER" && "border-amber-500/30"
          )}
          style={{ animationDelay: "0.5s" }}
        >
          {/* Gradient mesh for SUPER role */}
          {user?.role === "SUPER" && (
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-orange-500/5" />
          )}

          <CardHeader className="pb-4 relative">
            <div className="flex items-center gap-4">
              <div className={cn(
                "p-3 rounded-xl",
                user?.role === "SUPER"
                  ? "bg-gradient-to-br from-amber-400/20 to-orange-500/10 border border-amber-500/20"
                  : "bg-primary/10 border border-primary/10"
              )}>
                <roleConfig.icon className={cn(
                  "h-6 w-6",
                  user?.role === "SUPER" ? "text-amber-500" : "text-primary"
                )} />
              </div>
              <div>
                <CardTitle className="text-lg font-display">{roleConfig.title}</CardTitle>
                <CardDescription className="mt-1">{roleConfig.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 relative">
            <div className="flex flex-wrap gap-2">
              {user?.role === "SUPER" && (
                <>
                  <Badge variant="outline" className="border-amber-500/30 text-amber-700 dark:text-amber-400">Manage Organizations</Badge>
                  <Badge variant="outline" className="border-amber-500/30 text-amber-700 dark:text-amber-400">Platform Analytics</Badge>
                  <Badge variant="outline" className="border-amber-500/30 text-amber-700 dark:text-amber-400">Configure Integrations</Badge>
                  <Badge variant="outline" className="border-amber-500/30 text-amber-700 dark:text-amber-400">System Settings</Badge>
                </>
              )}
              {user?.role === "ORGANIZATION" && (
                <>
                  <Badge variant="outline">Business Unit Management</Badge>
                  <Badge variant="outline">Product Configuration</Badge>
                  <Badge variant="outline">Card Inventory</Badge>
                </>
              )}
              {user?.role === "BUSINESS_UNIT" && (
                <>
                  <Badge variant="outline">Issue Cards</Badge>
                  <Badge variant="outline">Employee Management</Badge>
                  <Badge variant="outline">Spending Controls</Badge>
                </>
              )}
              {user?.role === "FINANCE" && (
                <>
                  <Badge variant="outline">Financial Reports</Badge>
                  <Badge variant="outline">Transaction History</Badge>
                  <Badge variant="outline">Compliance Data</Badge>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Currency Converter */}
      <div className="animate-reveal-up" style={{ animationDelay: "0.5s" }}>
        <CurrencyConverter surface="admin" />
      </div>

      {/* Quick Stats Footer */}
      <div className="flex items-center justify-between pt-6 border-t border-border/50 text-sm text-muted-foreground animate-reveal-up" style={{ animationDelay: "0.6s" }}>
        <p>Last updated: <span className="font-mono">{new Date().toLocaleTimeString()}</span></p>
        <div className="flex items-center gap-4">
          <a href="https://vedalogic.betteruptime.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Status
          </a>
          <p className="font-medium">VedaLogic Card Management Platform <span className="text-primary">v1.0.0-rc.3</span></p>
        </div>
      </div>

      {/* Low Balance Alert Configuration Dialog */}
      <Dialog open={alertConfigOpen} onOpenChange={setAlertConfigOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-500" />
              Low Balance Alert
            </DialogTitle>
            <DialogDescription>
              Get notified when your remaining balance drops below a threshold.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Enable Alert</Label>
                <p className="text-xs text-muted-foreground">Show a warning banner on the dashboard</p>
              </div>
              <Switch checked={alertEnabled} onCheckedChange={setAlertEnabled} />
            </div>
            {alertEnabled && (
              <div className="space-y-2">
                <Label>Alert Threshold Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={alertAmountInput}
                    onChange={(e) => setAlertAmountInput(e.target.value)}
                    placeholder="e.g. 5000"
                    className="pl-9"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  An alert will appear when the remaining balance is at or below this amount.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertConfigOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAlert} disabled={saveAlertMutation.isPending}>
              {saveAlertMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
